import envConfig from '@hulu/env-config';
import { useLocation, useNavigate } from '@hulu/react-router-dom';
import { Auth } from 'aws-amplify';
import { useContext, useEffect } from 'react';

import { RoutePath } from '../../constants';
import { GhostModeContext } from '../../context/GhostMode/GhostMode';
import { useAWSCognitoUser } from '../../hooks';
import { MFAMethod } from '../../hooks/useMFA/constants';
import { UserInfo } from '../../models/UserAWSCognitoInfo';
import { createRedirectSearchPath, needToRedirectToLogin } from '../../utils/redirect';

type Props = {
  children: JSX.Element;
};

export const ProtectedRoute = ({ children }: Props): JSX.Element => {
  const navigate = useNavigate();
  const prevLocation = useLocation();
  const { isGhostMode } = useContext(GhostModeContext);
  const { getUserPreferredMFAMethod } = useAWSCognitoUser();

  useEffect(() => {
    if (!isGhostMode) {
      const navigateToLogout = (): void => {
        if (needToRedirectToLogin(window.location.href)) {
          navigate({
            pathname: `/${RoutePath.LOGOUT}`,
            search: createRedirectSearchPath(prevLocation.pathname, prevLocation.search),
          });
        }
      };

      const checkUserIsValid = async (): Promise<void> => {
        try {
          const cognitoUser = await Auth.currentAuthenticatedUser();
          if (!cognitoUser) {
            navigateToLogout();

            return;
          }

          const session = cognitoUser.getSignInUserSession();

          if (session && session.isValid()) {
            const user: UserInfo = session.getIdToken().decodePayload() as UserInfo;

            if (!user?.ams_user_id) {
              navigateToLogout();
            }
          } else {
            navigateToLogout();
          }

          if (envConfig.REACT_APP_DISABLE_2FA !== 'true') {
            const preferredMethod = await getUserPreferredMFAMethod(cognitoUser);
            if (preferredMethod === MFAMethod.NOMFA) {
              navigateToLogout();
            }
          }
        } catch {
          navigateToLogout();
        }
      };

      void checkUserIsValid();
    }
  }, [navigate, prevLocation.search, prevLocation.pathname, isGhostMode, getUserPreferredMFAMethod]);

  return children;
};
